
import Vue from 'vue';
import Component from 'vue-class-component';
import {Prop, Watch} from 'vue-property-decorator';

// Limits of the helpdesk (WhatsApp Cloud API): 4096 chars of text, 16 MB per file and 5 MB for
// images (jpeg/png only). The backend validates them again; here we stop the obvious cases early.
const MAX_TEXT = 4096;
const MAX_FILE_BYTES = 16 * 1024 * 1024;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const ACCEPT = 'image/jpeg,image/png,audio/aac,audio/mp4,audio/mpeg,audio/amr,audio/ogg,video/mp4,video/3gpp,'
  + 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,'
  + 'application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain';

// Message box of a conversation. It only emits; the view sends and tells it when it's done (`sent`).
@Component({name: 'WhatsappComposer'})
export default class WhatsappComposer extends Vue {
  @Prop({type: Boolean, default: true}) windowOpen!: boolean;
  @Prop({default: null}) windowExpiresAt!: string | null;
  @Prop({type: Boolean, default: false}) sending!: boolean;
  @Prop({type: Boolean, default: false}) disabled!: boolean;
  // Bumped by the parent after a successful send, to clear the box.
  @Prop({default: 0}) sentCount!: number;

  text = '';
  file: File | null = null;
  maxLength = MAX_TEXT;
  accept = ACCEPT;

  get canSend(): boolean {
    return !!this.file || this.text.trim().length > 0;
  }

  get fileSize(): string {
    if (!this.file) return '';
    const kb = this.file.size / 1024;
    return kb < 1024 ? Math.round(kb) + ' KB' : (kb / 1024).toFixed(1).replace('.', ',') + ' MB';
  }

  get windowExpiresLabel(): string {
    if (!this.windowExpiresAt) return '';
    const d = new Date(this.windowExpiresAt);
    return d.toLocaleDateString('es-ES', {day: '2-digit', month: '2-digit'}) + ' '
      + d.toLocaleTimeString('es-ES', {hour: '2-digit', minute: '2-digit'});
  }

  @Watch('sentCount')
  onSent() {
    this.text = '';
    this.clearFile();
  }

  send() {
    if (this.disabled || this.sending || !this.canSend) return;
    if (this.file) this.$emit('send-media', {file: this.file, caption: this.text.trim()});
    else this.$emit('send-text', this.text.trim());
  }

  pickFile() {
    (this.$refs.fileInput as HTMLInputElement).click();
  }

  onFile(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    input.value = '';
    if (!file) return;
    if (file.size > MAX_FILE_BYTES) {
      this.$uiBus.emit('alert', this.$t('helpdesk.whatsapp.composer.fileTooBig'));
      return;
    }
    if (file.type.indexOf('image/') === 0 && file.size > MAX_IMAGE_BYTES) {
      this.$uiBus.emit('alert', this.$t('helpdesk.whatsapp.composer.imageTooBig'));
      return;
    }
    this.file = file;
  }

  clearFile() {
    this.file = null;
  }
}
